import { info2_outline_16 as info16, userOutline_16 as user16 } from "@trustpilot/icons";
import { useTranslations } from "ilenia";
import React, { FC } from "react";

import { trackingConst } from "../../../constants";
import { useAnalytics } from "../../../utils/analytics";
import { NavMenuHeader } from "../common/navMenuHeader";
import Menu from "../menu";
import { NavIcon } from "../styled";
import AccountManager from "./accountManager";
import TechSupport from "./techSupport";
import * as Types from "./types";

export const ariaLabelTechSupport = "tech-support";
export const ariaLabelAccountManager = "account-manager";
const menuPanelId = "help-panel";

const Help: FC<Types.Help> = ({
  accountmanager,
  hasLiveChatFeature,
  toggled,
  toggle,
  businessUserCountry,
  businessUnitCountry,
  addressCountryCode,
}) => {
  const [translations] = useTranslations();
  const analytics = useAnalytics();

  const profileCountry = addressCountryCode || "";
  const isInAustralianTimezone = Boolean(
    [businessUserCountry, businessUnitCountry, profileCountry].find((c) => c === "AU" || c === "NZ"),
  );

  const toggleHelpMenu = React.useCallback(() => {
    if (!toggled) {
      analytics.track(trackingConst.events.buttonClicked, {
        context: "Business User Settings",
        name: "Open Help Menu",
        placement: "header",
      });
    }
    toggle();
  }, [analytics, toggled]);

  const openChatWidget = React.useCallback(() => {
    let name = "request-call-from-support";
    if (window.embeddedservice_bootstrap) {
      name = "open-salesforce-chat";
      window.embeddedservice_bootstrap.utilAPI.launchChat();
    }

    analytics.track(trackingConst.events.linkClicked, {
      name,
      action: "Open support widget",
      placement: "header",
    });
  }, [toggleHelpMenu, analytics]);

  const startChat = React.useCallback(() => {
    openChatWidget();
  }, []);

  const onLinkClicked = React.useCallback(
    (e) => {
      const url = e.currentTarget.href;
      let name = "go-to-support-for-business";

      if (url.includes("sections/200460303")) {
        name = "go-to-getting-started-guide";
      } else if (url.includes("sections/360003158500")) {
        name = "go-to-submit-a-request";
      }

      analytics.track(trackingConst.events.linkClicked, {
        action: "Go to support page",
        name,
        placement: "header",
        target: url,
      });
      toggleHelpMenu();
    },
    [toggleHelpMenu, analytics],
  );

  return (
    <Menu
      toggled={toggled}
      toggle={toggleHelpMenu}
      hideOnMobile
      target={
        <NavIcon
          ariaLabel={translations["BusinessUserSettings.help.information"]}
          icon={info16}
          aria-expanded={toggled}
          aria-controls={menuPanelId}
        />
      }
      testid="businessUserHelp"
      id={menuPanelId}
    >
      <section aria-labelledby={ariaLabelTechSupport}>
        <NavMenuHeader
          ariaLabel={ariaLabelTechSupport}
          headingLocoKey="BusinessUserSettings.help.information"
          icon={info16}
        />

        <TechSupport
          showLiveChatFeature={hasLiveChatFeature && !isInAustralianTimezone}
          onLinkClicked={onLinkClicked}
          startChat={startChat}
        />
      </section>
      {accountmanager && (
        <section aria-labelledby={ariaLabelAccountManager}>
          <NavMenuHeader
            ariaLabel={ariaLabelAccountManager}
            icon={user16}
            headingLocoKey="BusinessUserSettings.help.accountManager"
            borderBottom={false}
          />
          <AccountManager accountmanager={accountmanager} />
        </section>
      )}
    </Menu>
  );
};

export default Help;
